import {
  useState,
  useEffect,
  FocusEvent,
  // MouseEvent,
  KeyboardEvent
} from 'react';

import { Cell } from '.';
import warningIcon from 'assets/icons/warning.svg';
import { COMMA_TABLE_KEYS } from './utils/constants';
import { CellMeta, RowWithId, TableKeys } from './types';
import { truncateToDecimal } from 'utils/helpers/truncateToDecimal';

export const EditableCell = <T, K extends keyof T>({
  cell,
  hasError,
  handleBlur,
  initialValue
}: {
  cell: Cell<T, K>;
  initialValue: T[K];
  handleBlur: (value: T[K], cell: Cell<T, K>) => void;
  hasError: (cell: Cell<T, K>, isHighlighted: boolean) => boolean;
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [highlighted, setHighlighted] = useState(false);

  const [value, setValue] = useState<string | null>(() =>
    cell.getValue() != null ? String(cell.getValue()) : null
  );

  const showError = hasError(cell, highlighted);
  const meta = (cell.row.original as { meta: CellMeta })['meta'];
  const title = ((cell.row.original as { title: K })['title'] as string) || '';
  const tooltip = `${title}\nInitial Value: ${initialValue || 'N/A'} `;

  useEffect(() => {
    const externalValue = cell.getValue();

    setValue(prev => {
      if (prev === externalValue) {
        return prev;
      }

      return externalValue != null ? String(externalValue) : '';
    });
  }, [cell]);

  useEffect(() => {
    if (value !== null && value !== initialValue) {
      setHighlighted(true);
    } else {
      setHighlighted(false);
    }
  }, [value, initialValue]);

  const formatDecimalValue = (val: string | null): string => {
    if (
      !val ||
      isFocused ||
      isHovered ||
      val === '' ||
      !meta?.allowDecimal ||
      isNaN(parseFloat(val)) ||
      COMMA_TABLE_KEYS.includes(
        (cell.row.original as RowWithId).id as TableKeys
      )
    ) {
      return val || '';
    }

    const num = parseFloat(val);

    return truncateToDecimal(num, 1).toFixed(1);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/,/g, '').replace(/ /g, '');

    let pattern: RegExp;

    if (meta?.isNegative) {
      pattern = meta.allowDecimal ? /^-?\d*\.?\d{0,3}$/ : /^-?\d*$/;
    } else {
      pattern = meta?.allowNegative
        ? meta?.allowDecimal
          ? /^-?\d*\.?\d{0,3}$/
          : /^-?\d*$/
        : meta?.allowDecimal
        ? /^\d*\.?\d{0,3}$/
        : /^\d*$/;
    }

    if (!pattern.test(val)) {
      return;
    }

    if (meta?.isNegative && val !== '' && val !== '-') {
      const numVal = parseFloat(val);

      if (val === '0.' || (!isNaN(numVal) && numVal > 0)) {
        return;
      }
    }

    setValue(val);
  };

  const onBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    setIsFocused(false);

    handleBlur(
      e.target.value === ''
        ? (null as T[K])
        : (Number(e.target.value.replace(/,/g, '')) as T[K]),
      cell
    );
  };

  const onFocus = () => {
    setIsFocused(true);
  };

  const onMouseEnter = () => {
    setIsHovered(true);
  };

  const onMouseLeave = () => {
    setIsHovered(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      const inputs = Array.from(
        document.querySelectorAll('input[tabindex="0"]')
      );
      const idx = inputs.indexOf(e.currentTarget);
      let nextIdx = idx;
      const columns = cell.row.getVisibleCells().length;
      switch (e.key) {
        case 'ArrowRight':
          nextIdx = idx + 1;
          break;
        case 'ArrowLeft':
          nextIdx = idx - 1;
          break;
        case 'ArrowDown':
          nextIdx = idx + columns - 1;
          break;
        case 'ArrowUp':
          nextIdx = idx - columns + 1;
          break;
      }
      if (inputs[nextIdx]) {
        const nextInput = inputs[nextIdx] as HTMLInputElement;

        nextInput.focus();

        setTimeout(() => {
          nextInput.select();
        }, 0);
      }
    }
  };

  // const onClick = (e: MouseEvent<HTMLInputElement, globalThis.MouseEvent>) => {
  //   (e.target as HTMLInputElement).select();
  // };

  const displayValue = formatDecimalValue(value);

  return (
    <div className='relative'>
      <input
        tabIndex={0}
        value={displayValue}
        title={tooltip}
        onBlur={onBlur}
        onFocus={onFocus}
        // onClick={onClick}
        onChange={onChange}
        aria-label={tooltip}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        readOnly={meta?.allowEdit === false}
        className={`w-full h-10 ps-2 outline-none rounded focus:font-bold focus:text-base truncate transition-colors duration-1000 font-openSans
        ${highlighted ? 'bg-[#D5F0DD] dark:bg-[#D5F0DD20]' : 'bg-transparent'}
        ${meta?.allowEdit === false ? 'cursor-default' : ''}
        ${
          showError ? 'bg-[#D52F241A] border border-[#D52F24]' : 'border-none'
        }`}
      />

      {showError && (
        <img
          alt='warning'
          src={warningIcon}
          className='absolute top-2 right-2'
          title='𝗙𝗹𝗮𝗴 𝗳𝗼𝗿 𝗗𝗮𝘁𝗮 𝗜𝗻𝗰𝗼𝗻𝘀𝗶𝘀𝘁𝗲𝗻𝗰𝘆&#10;Please review before finalizing'
        />
      )}
    </div>
  );
};
